/**
 * The browser's microphone as the recorder's device (UC-8, light, slice 02): `getUserMedia` for
 * mono speech as it is — no echo canceller or noise gate chewing on the second voice across the
 * table, only the gain kept level — a `MediaRecorder` on it at a speech bitrate, and an analyser
 * on the same stream for the meter. The recorder hands over a slice every five seconds, so a
 * recording that ends on its own loses at most the last of them. One recording is one
 * uninterrupted run: the recorder leaves a pause out of the file by itself.
 *
 * A caller that records something else may ask for other microphone settings, another bitrate
 * and the true level beside the meter's bar (`BrowserRecorderOptions`: the voice sample does,
 * ADR-0062). Without options it is the questioning's recorder, as it always was.
 */
import {
  type LevelSignal,
  meterLevel,
  rmsOfBytes,
  rmsOfFloats,
  signalOfFloats,
} from './level-meter'
import {
  type RecorderDevice,
  type RecorderDeviceEvents,
  RecorderUnsupportedError,
} from './recorder-controller'
import { pickRecorderMimeType } from './recorder-format'

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: true,
}

/** Speech in Opus or AAC: about 22 MB an hour. */
const AUDIO_BITS_PER_SECOND = 48_000

/** How often the recorder hands over what it has. */
const TIMESLICE_MS = 5_000

/** What a caller may ask of the recorder in place of the questioning's settings. */
export interface BrowserRecorderOptions {
  /**
   * The microphone settings asked for. They are wishes, never demands: a browser that does not
   * know one ignores it, and one that refuses them is asked again with the questioning's.
   */
  audio?: MediaTrackConstraints
  /** The bitrate asked for; a recorder that will not take it records at the questioning's. */
  audioBitsPerSecond?: number
  /** Also read the true level and the clipping sign (`RecorderDevice.signal`). */
  signal?: boolean
}

/** The container this browser records in; null when it cannot record here at all. */
function recorderMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof navigator === 'undefined') return null
  if (!navigator.mediaDevices?.getUserMedia) return null
  return pickRecorderMimeType(type => MediaRecorder.isTypeSupported(type))
}

/** Whether this browser can record here: it has a recorder, and the page is a secure context. */
export const canRecordHere = (): boolean => recorderMimeType() !== null

interface Meter {
  context: AudioContext
  analyser: AnalyserNode
  samples: Uint8Array<ArrayBuffer>
  /** The same frame as floats, for the true level; null unless it was asked for and can be read. */
  floats: Float32Array<ArrayBuffer> | null
}

/** The meter only reads the stream a few times a second, for hours: the low-power setting. */
const METER_CONTEXT: AudioContextOptions = { latencyHint: 'playback' }

/** The meter is a readout: where it cannot be opened, the recording runs without it. */
function openMeter(stream: MediaStream, signal = false): Meter | null {
  let context: AudioContext | null = null
  try {
    context = new AudioContext(METER_CONTEXT)
    const analyser = context.createAnalyser()
    analyser.fftSize = 1024
    context.createMediaStreamSource(stream).connect(analyser)
    void context.resume().catch(() => undefined)
    const measures = signal && typeof analyser.getFloatTimeDomainData === 'function'
    const floats = measures ? new Float32Array(analyser.fftSize) : null
    return { context, analyser, samples: new Uint8Array(analyser.fftSize), floats }
  } catch {
    void context?.close().catch(() => undefined)
    return null
  }
}

function readLevel(meter: Meter | null): number {
  if (!meter) return 0
  // Voice samples need the quiet signal too: 8-bit frames flatten it into quantization noise.
  if (meter.floats) {
    meter.analyser.getFloatTimeDomainData(meter.floats)
    return meterLevel(rmsOfFloats(meter.floats))
  }
  meter.analyser.getByteTimeDomainData(meter.samples)
  return meterLevel(rmsOfBytes(meter.samples))
}

/** The true level of the frame the analyser holds now; null where the meter cannot say. */
function readSignal(meter: Meter | null): LevelSignal | null {
  if (!meter?.floats) return null
  meter.analyser.getFloatTimeDomainData(meter.floats)
  return signalOfFloats(meter.floats)
}

interface OpenMicrophone {
  stream: MediaStream
  recorder: MediaRecorder
  meter: Meter | null
}

/** Let everything go: no handler fires after this, and the browser's recording sign goes out. */
function release({ stream, recorder, meter }: OpenMicrophone): void {
  recorder.ondataavailable = null
  recorder.onstop = null
  recorder.onerror = null
  for (const track of stream.getTracks()) {
    track.onended = null
    track.stop()
  }
  void meter?.context.close().catch(() => undefined)
}

function listen({ stream, recorder }: OpenMicrophone, events: RecorderDeviceEvents): void {
  recorder.ondataavailable = event => {
    if (event.data.size > 0) events.onData(event.data)
  }
  recorder.onstop = () => events.onStop()
  recorder.onerror = () => events.onLost()
  // A call, a revoked permission, an unplugged headset: the track ends on its own.
  for (const track of stream.getAudioTracks()) track.onended = () => events.onLost()
}

function deviceOf(open: OpenMicrophone, mimeType: string, signal = false): RecorderDevice {
  const { recorder } = open
  let listening: RecorderDeviceEvents | null = null
  return {
    // Only where it was asked for: the questioning's device has no such reading.
    ...(signal && { signal: () => readSignal(open.meter) }),
    mimeType: recorder.mimeType || mimeType,
    start: events => {
      listening = events
      listen(open, events)
      recorder.start(TIMESLICE_MS)
    },
    pause: () => {
      if (recorder.state === 'recording') recorder.pause()
    },
    resume: () => {
      if (recorder.state === 'paused') recorder.resume()
    },
    stop: () => {
      // A recorder that already ended has no last slice left to hand over.
      if (recorder.state === 'inactive') listening?.onStop()
      else recorder.stop()
    },
    level: () => readLevel(open.meter),
    release: () => release(open),
  }
}

/** A browser that will not take the settings asked for; a refused microphone is not this. */
function refusesSettings(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name
  return (
    name === 'OverconstrainedError' ||
    name === 'ConstraintNotSatisfiedError' ||
    name === 'TypeError'
  )
}

/**
 * The microphone with the settings asked for. Settings of a caller's own that the browser will
 * not take are no reason to give no recording: it is asked once more with the questioning's.
 */
async function openStream(audio: MediaTrackConstraints | undefined): Promise<MediaStream> {
  const devices = navigator.mediaDevices
  if (!audio) return devices.getUserMedia({ audio: AUDIO_CONSTRAINTS })
  try {
    return await devices.getUserMedia({ audio })
  } catch (error) {
    if (!refusesSettings(error)) throw error
    return devices.getUserMedia({ audio: AUDIO_CONSTRAINTS })
  }
}

/** A recorder at the bitrate asked for, or — where it will not take that — at the questioning's. */
function newRecorder(stream: MediaStream, mimeType: string, bitrate?: number): MediaRecorder {
  const usual = { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
  if (bitrate === undefined) return new MediaRecorder(stream, usual)
  try {
    return new MediaRecorder(stream, { mimeType, audioBitsPerSecond: bitrate })
  } catch {
    return new MediaRecorder(stream, usual)
  }
}

/**
 * Ask for the microphone and put a recorder on it. Rejects with `RecorderUnsupportedError`
 * where the browser cannot record, and with what `getUserMedia` rejects with when the
 * microphone is refused or cannot be started. `options`: another recorder than the
 * questioning's (`BrowserRecorderOptions`).
 */
export async function openBrowserRecorder(
  options: BrowserRecorderOptions = {}
): Promise<RecorderDevice> {
  const mimeType = recorderMimeType()
  if (!mimeType) throw new RecorderUnsupportedError()
  const stream = await openStream(options.audio)
  try {
    const recorder = newRecorder(stream, mimeType, options.audioBitsPerSecond)
    const meter = openMeter(stream, options.signal)
    return deviceOf({ stream, recorder, meter }, mimeType, options.signal)
  } catch (error) {
    for (const track of stream.getTracks()) track.stop()
    throw error
  }
}
